import React, { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { FileSpreadsheet, Download, UploadCloud, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";

const ImportRequestsModal = ({ open, client, onClose, onImported }) => {
  const { toast } = useToast();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/requests/bulk-template", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = "request_template.xlsx"; a.click(); URL.revokeObjectURL(url);
    } catch { toast({ title: "Could not download template", variant: "destructive" }); }
  };

  const upload = async (file) => {
    if (!file || !client) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/clients/${client.id}/requests/bulk-upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast({ title: `Imported ${data.created_count} request(s)`, description: data.errors?.length ? `${data.errors.length} row(s) skipped.` : "All rows imported." });
      onImported && onImported();
    } catch (err) {
      toast({ title: "Import failed", description: err?.response?.data?.detail || "Use the provided template.", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const close = () => { setResult(null); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="import-requests-modal" className="sm:max-w-lg rounded-2xl">
        <DialogHeader>
          <div className="mb-2 flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 dark:bg-emerald-500/15"><FileSpreadsheet className="h-6 w-6 text-emerald-700 dark:text-emerald-300" /></div>
          <DialogTitle className="text-2xl font-bold text-[#1a1a2e] dark:text-slate-100">Bulk import requests</DialogTitle>
          <DialogDescription className="text-slate-500">Upload up to 100 requests for <strong>{client?.name}</strong> from an Excel/CSV file.</DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-4">
          <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/50">
            <p className="text-sm font-medium text-[#1a1a2e] dark:text-slate-200">Step 1 — Get the template</p>
            <p className="mt-1 text-xs text-slate-500">Columns: Title*, Description, Area, Project, Deadline (YYYY-MM-DD), Tasks (semicolon-separated).</p>
            <Button data-testid="download-template-btn" onClick={downloadTemplate} variant="outline" size="sm" className="mt-3 rounded-xl dark:border-slate-700"><Download className="mr-1.5 h-4 w-4" /> Download .xlsx template</Button>
          </div>

          <div className="rounded-xl border border-slate-100 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/50">
            <p className="text-sm font-medium text-[#1a1a2e] dark:text-slate-200">Step 2 — Upload the filled file</p>
            <div onClick={() => fileRef.current?.click()} className="mt-3 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 p-5 text-center hover:border-slate-300 dark:border-slate-700">
              {busy ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" /> : <UploadCloud className="h-6 w-6 text-slate-400" />}
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">Click to choose an .xlsx or .csv file</p>
              <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" data-testid="import-file-input" onChange={(e) => upload(e.target.files?.[0])} />
            </div>
          </div>

          {result && (
            <div data-testid="import-result" className="rounded-xl border border-slate-100 p-4 dark:border-slate-800">
              <p className="flex items-center gap-2 text-sm font-semibold text-emerald-700 dark:text-emerald-300"><CheckCircle2 className="h-4 w-4" /> {result.created_count} request(s) created</p>
              {result.errors?.length > 0 && (
                <div className="mt-2">
                  <p className="flex items-center gap-1.5 text-xs font-medium text-amber-600"><AlertTriangle className="h-3.5 w-3.5" /> {result.errors.length} row(s) skipped</p>
                  <ul className="mt-1 max-h-24 overflow-y-auto text-xs text-slate-500">
                    {result.errors.map((e, i) => <li key={i}>Row {e.row}: {e.reason}</li>)}
                  </ul>
                </div>
              )}
              <p className="mt-3 text-xs text-slate-500">Imported requests were created without emailing. Select them in the Command Center and use <strong>Notify clients</strong> to send emails.</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImportRequestsModal;
